class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-search-results]');
    this.timer = null;

    if (!this.input || !this.results) return;

    this.input.addEventListener('input', () => {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.search(), 200);
    });
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
      if (event.key === 'ArrowDown' && !this.results.hidden) {
        event.preventDefault();
        this.results.querySelector('a')?.focus();
      }
    });
    this.results.addEventListener('keydown', (event) => this.navigateResults(event));
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });
  }

  async search() {
    const term = this.input.value.trim();

    if (term.length < 2) {
      this.close();
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.input.setAttribute('aria-busy', 'true');

    const parameters = new URLSearchParams({
      q: term,
      'resources[type]': 'product,query,collection,page,article',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });

    try {
      const response = await fetch(`${this.dataset.url}?${parameters}`, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('[data-predictive-search-content]');

      this.results.innerHTML = content?.outerHTML || '';
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    } finally {
      this.input.removeAttribute('aria-busy');
    }
  }

  navigateResults(event) {
    if (!['ArrowDown', 'ArrowUp', 'Escape'].includes(event.key)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.input.focus();
      return;
    }

    const links = [...this.results.querySelectorAll('a')];
    const currentIndex = links.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    event.preventDefault();
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    links[(currentIndex + direction + links.length) % links.length]?.focus();
  }

  close() {
    this.results.hidden = true;
    this.results.replaceChildren();
    this.input.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
